import { useState, useEffect, useContext } from 'react'
import { AuthContext } from '../contexts/AuthContext'
import './InventoryCollections.css'

const API = process.env.REACT_APP_API || ''

const apiFetch = async (path, opts = {}) => {
    const res = await fetch(API + path, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
        ...opts,
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`)
    return data
}

const slugify = (s) =>
    s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

const TYPE_LABELS = {
    permanent: 'Permanente',
    seasonal:  'Saisonnière',
    drop:      'Drop',
    collab:    'Collab',
}

const InventoryCollections = () => {
    const { user } = useContext(AuthContext)
    const brandId = user?.brand

    const [collections, setCollections] = useState([])
    const [loading, setLoading] = useState(true)
    const [selected, setSelected] = useState(null)
    const [detailLoading, setDetailLoading] = useState(false)

    const [showCreate, setShowCreate] = useState(false)
    const [createForm, setCreateForm] = useState({ name: '', slug: '', description: '', type: 'permanent' })
    const [creating, setCreating] = useState(false)

    // Produits disponibles pour ajouter à la collection
    const [allProducts, setAllProducts] = useState([])
    const [showAddProduct, setShowAddProduct] = useState(false)
    const [addingProductId, setAddingProductId] = useState('')
    const [addingProduct, setAddingProduct] = useState(false)

    const [archiving, setArchiving] = useState(false)

    const loadCollections = async () => {
        if (!brandId) return
        setLoading(true)
        try {
            const data = await apiFetch(`/brand/${brandId}/collections`)
            setCollections((data.collections || []).filter(c => !c.archivedAt))
        } catch { setCollections([]) }
        finally { setLoading(false) }
    }

    const selectCollection = async (id) => {
        setDetailLoading(true)
        try {
            const data = await apiFetch(`/brand/${brandId}/collections/${id}`)
            setSelected(data)
        } catch (err) { console.error(err) }
        finally { setDetailLoading(false) }
    }

    const loadProducts = async () => {
        try {
            const data = await apiFetch(`/brand/${brandId}/products`)
            setAllProducts(data.products || [])
        } catch { setAllProducts([]) }
    }

    useEffect(() => { loadCollections() }, [brandId])

    const handleCreate = async (e) => {
        e.preventDefault()
        setCreating(true)
        try {
            await apiFetch(`/brand/${brandId}/collections`, {
                method: 'POST',
                body: JSON.stringify({
                    name: createForm.name,
                    slug: createForm.slug || slugify(createForm.name),
                    description: createForm.description,
                    type: createForm.type,
                }),
            })
            setShowCreate(false)
            setCreateForm({ name: '', slug: '', description: '', type: 'permanent' })
            await loadCollections()
        } catch (err) { alert(err.message) }
        finally { setCreating(false) }
    }

    const openAddProduct = async () => {
        await loadProducts()
        setAddingProductId('')
        setShowAddProduct(true)
    }

    const handleAddProduct = async () => {
        if (!addingProductId) return
        setAddingProduct(true)
        try {
            const current = selected.productIds || []
            if (current.includes(addingProductId)) { setShowAddProduct(false); return }
            await apiFetch(`/brand/${brandId}/collections/${selected._id}`, {
                method: 'PATCH',
                body: JSON.stringify({ productIds: [...current, addingProductId] }),
            })
            await selectCollection(selected._id)
            setShowAddProduct(false)
        } catch (err) { alert(err.message) }
        finally { setAddingProduct(false) }
    }

    const handleRemoveProduct = async (productId) => {
        try {
            const current = (selected.productIds || []).filter(id => String(id) !== String(productId))
            await apiFetch(`/brand/${brandId}/collections/${selected._id}`, {
                method: 'PATCH',
                body: JSON.stringify({ productIds: current }),
            })
            await selectCollection(selected._id)
        } catch (err) { alert(err.message) }
    }

    const handleArchive = async () => {
        if (!window.confirm('Archiver cette collection ?')) return
        setArchiving(true)
        try {
            await apiFetch(`/brand/${brandId}/collections/${selected._id}`, { method: 'DELETE' })
            setSelected(null)
            await loadCollections()
        } catch (err) { alert(err.message) }
        finally { setArchiving(false) }
    }

    // ── DÉTAIL ──────────────────────────────────────────────────────────────

    if (detailLoading) return <div className="icoll-loading">Chargement...</div>

    if (selected) {
        const productsList = selected.productIds || []
        return (
            <div className="icoll-detail">
                <div className="icoll-detail-header">
                    <button className="icoll-back-btn" onClick={() => setSelected(null)}>← Retour</button>
                    <h2 className="icoll-detail-title">{selected.name}</h2>
                    <span className="icoll-type-badge">{TYPE_LABELS[selected.type]}</span>
                </div>

                {selected.description && <p className="icoll-description">{selected.description}</p>}

                <div className="icoll-section">
                    <div className="icoll-section-header">
                        <h3>Produits ({productsList.length})</h3>
                        {!showAddProduct && (
                            <button className="icoll-btn-primary" onClick={openAddProduct}>+ Ajouter un produit</button>
                        )}
                    </div>

                    {showAddProduct && (
                        <div className="icoll-add-product-row">
                            <select
                                value={addingProductId}
                                onChange={e => setAddingProductId(e.target.value)}
                            >
                                <option value="">— Choisir un produit —</option>
                                {allProducts
                                    .filter(p => !productsList.map(String).includes(String(p._id)))
                                    .map(p => <option key={p._id} value={p._id}>{p.name}</option>)
                                }
                            </select>
                            <button
                                className="icoll-btn-primary"
                                onClick={handleAddProduct}
                                disabled={addingProduct || !addingProductId}
                            >
                                {addingProduct ? '...' : 'Ajouter'}
                            </button>
                            <button className="icoll-btn-outline" onClick={() => setShowAddProduct(false)}>
                                Annuler
                            </button>
                        </div>
                    )}

                    {productsList.length === 0 ? (
                        <p className="icoll-empty">Aucun produit dans cette collection.</p>
                    ) : (
                        <div className="icoll-products-list">
                            {productsList.map(pid => {
                                const p = allProducts.find(ap => String(ap._id) === String(pid))
                                return (
                                    <div key={String(pid)} className="icoll-product-row">
                                        <span>{p ? p.name : String(pid)}</span>
                                        <button className="icoll-btn-remove" onClick={() => handleRemoveProduct(pid)}>
                                            Retirer
                                        </button>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>

                <div className="icoll-danger-zone">
                    <button className="icoll-btn-danger" onClick={handleArchive} disabled={archiving}>
                        {archiving ? 'Archivage...' : 'Archiver cette collection'}
                    </button>
                </div>
            </div>
        )
    }

    // ── LISTE ──────────────────────────────────────────────────────────────

    if (loading) return <div className="icoll-loading">Chargement...</div>

    return (
        <div className="icoll-page">
            <div className="icoll-header">
                <h2>Collections inventaire</h2>
                <button className="icoll-btn-primary" onClick={() => setShowCreate(s => !s)}>
                    {showCreate ? 'Annuler' : '+ Nouvelle collection'}
                </button>
            </div>

            {showCreate && (
                <form className="icoll-create-form" onSubmit={handleCreate}>
                    <div className="icoll-field">
                        <label>Nom *</label>
                        <input
                            value={createForm.name}
                            onChange={e => setCreateForm(f => ({
                                ...f, name: e.target.value, slug: slugify(e.target.value)
                            }))}
                            placeholder="Nom de la collection"
                            required
                        />
                    </div>
                    <div className="icoll-field">
                        <label>Type</label>
                        <select value={createForm.type} onChange={e => setCreateForm(f => ({ ...f, type: e.target.value }))}>
                            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </div>
                    <div className="icoll-field">
                        <label>Description (optionnel)</label>
                        <textarea
                            rows={2}
                            value={createForm.description}
                            onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                            placeholder="Description"
                        />
                    </div>
                    <div className="icoll-form-actions">
                        <button type="submit" className="icoll-btn-primary" disabled={creating}>
                            {creating ? 'Création...' : 'Créer'}
                        </button>
                    </div>
                </form>
            )}

            {collections.length === 0 ? (
                <p className="icoll-empty">Aucune collection. Créez votre première collection d'inventaire.</p>
            ) : (
                <div className="icoll-list">
                    {collections.map(c => (
                        <div key={c._id} className="icoll-list-item" onClick={() => selectCollection(c._id)}>
                            <div className="icoll-list-item-main">
                                <span className="icoll-list-item-name">{c.name}</span>
                                <span className="icoll-list-item-meta">
                                    {c.productIds?.length ?? 0} produit{c.productIds?.length !== 1 ? 's' : ''}
                                    {c.description ? ` · ${c.description}` : ''}
                                </span>
                            </div>
                            <div className="icoll-list-item-right">
                                <span className="icoll-type-badge">{TYPE_LABELS[c.type]}</span>
                                <span className="icoll-arrow">›</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default InventoryCollections
